import React, { useState, useEffect, useMemo } from "react";
import ReactDOM from "react-dom";
import { message } from "antd";
import { getApi } from "../../../../../api/api_instance";
import { API_URLS } from "../../../../../api/apiUrls";
import QuestionInput from "./QuestionInput";
import PopupCard from "../../../../Auth/PopupCard";
import {
  GuidanceBlock,
  evalCondition,
  TRIP_SEP,
} from "./displayConditionUtils";

export const useTableDefinition = (tableRowTypeId, isEmployee) => {
  const [definition, setDefinition] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    console.log(
      `useTableDefinition - tableRowTypeId:`,
      tableRowTypeId,
      "Type:",
      typeof tableRowTypeId,
    );
    if (tableRowTypeId && typeof tableRowTypeId === "string") {
      const load = async () => {
        setLoading(true);
        try {
          const apiUrl = isEmployee
            ? API_URLS.GET_TABLE_ROW_TYPE_DEFINITION(tableRowTypeId)
            : API_URLS.GET_CUSTOMER_TABLE_ROW_TYPE_DEFINITION(tableRowTypeId);

          const res = await getApi({ apiUrl });
          if (res && res.success) {
            setDefinition(res.data);
          }
        } catch (err) {
          console.error(`Failed table definition load ${tableRowTypeId}`, err);
        }
        setLoading(false);
      };
      load();
    }
  }, [tableRowTypeId]);

  return { definition, loading };
};

const QuestionTable = ({
  isEmployee,
  question,
  tableId,
  answers: answersProp,
  setAnswers,
  onQuestionLoaded,
  divisionId,
}) => {
  const { definition, loading } = useTableDefinition(
    question.tableRowTypeId,
    isEmployee,
  );
  const [showPopup, setShowPopup] = useState(false);
  const [popupData, setPopupData] = useState({
    type: "error",
    title: "Validation Error",
    message: "",
  });

  useEffect(() => {
    if (!showPopup) return;
    const timer = setTimeout(() => setShowPopup(false), 3000);
    return () => clearTimeout(timer);
  }, [showPopup]);

  const fields = useMemo(() => {
    const rawFields = definition?.fields || [];
    const hasState = rawFields.some(
      (f) =>
        f.fieldId.toLowerCase().includes("state") ||
        String(f.fieldLabel).toLowerCase().includes("state"),
    );
    const hasCity = rawFields.some(
      (f) =>
        f.fieldId.toLowerCase().includes("city") ||
        String(f.fieldLabel).toLowerCase().includes("city"),
    );
    if (hasState && !hasCity) {
      const stateField = rawFields.find(
        (f) =>
          f.fieldId.toLowerCase().includes("state") ||
          String(f.fieldLabel).toLowerCase().includes("state"),
      );
      const prefix = stateField.fieldId.includes("_state")
        ? stateField.fieldId.replace(/_state.*$/, "")
        : "";
      const cityFieldId = prefix ? `${prefix}_city` : "city";
      return [
        ...rawFields,
        {
          fieldId: cityFieldId,
          fieldLabel: "City",
          fieldType: "text",
          displayCondition: [
            {
              triggerQuestionId: stateField.fieldId,
              operator: "exists",
              value: true,
            },
          ],
        },
      ];
    }
    return rawFields;
  }, [definition?.fields]);

  useEffect(() => {
    if (definition && onQuestionLoaded) {
      fields.forEach((field) => {
        onQuestionLoaded(
          {
            ...field,
            questionId: field.fieldId,
            questionType: field.fieldType,
            questionText: field.fieldLabel,
          },
          divisionId,
        );
      });
    }
  }, [definition, fields, onQuestionLoaded, divisionId]);

  const categoryFields = useMemo(
    () =>
      fields.filter(
        (f) =>
          (f.fieldType === "multiselect" ||
            f.fieldType === "multi-select" ||
            f.type === "multiselect" ||
            f.type === "multi-select" ||
            f.questionType === "multiselect" ||
            f.questionType === "multi-select") &&
          (!f.displayCondition || f.displayCondition.length === 0) &&
          fields.some((other) =>
            other.displayCondition?.some(
              (cond) =>
                (cond.triggerQuestionId === f.fieldId ||
                  cond.triggerFieldId === f.fieldId) &&
                (cond.operator === "exists" || cond.operator === "eq"),
            ),
          ),
      ),
    [fields],
  );

  const isCategorized = categoryFields.length > 0;

  const globalFields = useMemo(
    () =>
      fields.filter(
        (f) =>
          (!f.displayCondition || f.displayCondition.length === 0) &&
          !categoryFields.find((cf) => cf.fieldId === f.fieldId),
      ),
    [fields, categoryFields],
  );

  const selectedCategories = useMemo(() => {
    if (!isCategorized) return ["default"];
    const allCats = new Set();
    categoryFields.forEach((cf) => {
      // Check both prefixed (table-specific) and global answer keys
      const vals =
        answersProp[`${tableId}_${cf.fieldId}`] ||
        answersProp[cf.fieldId] ||
        [];
      const valArray = Array.isArray(vals) ? vals : [vals];
      valArray.forEach((v) => {
        if (v) allCats.add(v);
      });
    });
    return Array.from(allCats);
  }, [categoryFields, answersProp, tableId, isCategorized]);

  const allCatKeys = useMemo(() => {
    const keys = [];
    if (!isCategorized) {
      keys.push("default");
    } else {
      if (globalFields.length > 0) keys.push("default");
      // Always include "default" if it has data but isn't in the list
      const tableData = answersProp[tableId] || {};
      if (
        tableData["default"] &&
        tableData["default"].length > 0 &&
        !keys.includes("default")
      ) {
        keys.push("default");
      }
      selectedCategories.forEach((cat) => {
        if (!keys.includes(cat)) keys.push(cat);
      });

      // Ensure we always have at least one key to render
      if (keys.length === 0) keys.push("default");
    }

    console.log(`QuestionTable [${tableId}] - Keys:`, {
      isCategorized,
      categoryFields: categoryFields.map((cf) => cf.fieldId),
      selectedCategories,
      finalKeys: keys,
    });

    return keys;
  }, [isCategorized, globalFields, selectedCategories, answersProp, tableId]);

  const getValueFromRow = (rowValues, fieldId, itemValue = null) => {
    if (!rowValues) return undefined;

    // Helper to extract value from a matched option object or normal object
    const extractObjectVal = (val) => {
      if (val && typeof val === "object") {
        return val.value !== undefined
          ? val.value
          : val.answer !== undefined
            ? val.answer
            : val;
      }
      return val;
    };

    // Helper to find and extract matched item from an array of key-value objects
    const extractArrayVal = (arr) => {
      if (itemValue && itemValue !== "default") {
        const match = arr.find(
          (item) =>
            item &&
            (item.key === itemValue ||
              item.id === itemValue ||
              item.value === itemValue),
        );
        if (match) {
          return extractObjectVal(match);
        }
      }
      return arr;
    };

    // 1. Try exact match first (raw key)
    if (rowValues[fieldId] !== undefined) {
      let val = rowValues[fieldId];
      if (Array.isArray(val)) {
        return extractArrayVal(val);
      }
      return extractObjectVal(val);
    }

    // 2. Try suffixed key (for categorized data)
    const suffixedKey =
      itemValue && itemValue !== "default"
        ? `${fieldId}_${itemValue}`
        : `${fieldId}_default`;
    if (rowValues[suffixedKey] !== undefined) {
      let val = rowValues[suffixedKey];
      if (Array.isArray(val)) {
        return extractArrayVal(val);
      }
      return extractObjectVal(val);
    }

    // 3. Fallback: Search for any key that starts with fieldId (handles monthly suffixes like _jan)
    const fallbackKey = Object.keys(rowValues).find((k) =>
      k.startsWith(fieldId),
    );
    if (fallbackKey) {
      let val = rowValues[fallbackKey];
      if (Array.isArray(val)) {
        return extractArrayVal(val);
      }
      return extractObjectVal(val);
    }

    // 4. Search for suffixed fields stored as arrays of objects (e.g., percentage_each_category_metal_steel in percentage_each_category: [{key: 'metal_steel', value: 20}])
    for (const [key, val] of Object.entries(rowValues)) {
      if (Array.isArray(val) && fieldId.startsWith(`${key}_`)) {
        const suffix = fieldId.replace(`${key}_`, "");
        const match = val.find(
          (v) =>
            v && (v.key === suffix || v.id === suffix || v.value === suffix),
        );
        if (match) {
          if (match.value !== undefined && match.key !== undefined)
            return match.value;
          if (match.answer !== undefined) return match.answer;
          return match;
        }
      }
    }

    // 4. If rowValues is an array of objects, try to find a match for the context
    if (
      Array.isArray(rowValues) &&
      rowValues.length > 0 &&
      typeof rowValues[0] === "object"
    ) {
      if (itemValue && itemValue !== "default") {
        const match = rowValues.find(
          (item) =>
            item &&
            (item.key === itemValue ||
              item.id === itemValue ||
              item.value === itemValue),
        );
        if (match) {
          if (match.value !== undefined) return match.value;
          if (match.answer !== undefined) return match.answer;
          return match;
        }
      }
    }

    return undefined;
  };

  useEffect(() => {
    if (!definition || loading) return;

    setAnswers((prev) => {
      let incomingData = answersProp[tableId];
      // Normalize raw API response format to local format
      if (
        incomingData &&
        incomingData.tableRows &&
        Array.isArray(incomingData.tableRows)
      ) {
        incomingData = { default: incomingData.tableRows };
      }

      const localData = prev[tableId];
      const localRows = localData ? Object.values(localData).flat() : [];

      const isLocalEmpty =
        !localRows ||
        localRows.length === 0 ||
        localRows.every((row) => {
          const keys = Object.keys(row.values || {});
          return (
            keys.length === 0 ||
            keys.every((k) => {
              const val = row.values[k];
              return (
                val === undefined ||
                val === null ||
                val === "" ||
                (Array.isArray(val) && val.length === 0) ||
                val === 0 ||
                val === "0"
              );
            })
          );
        });

      const shouldSync =
        incomingData && (isLocalEmpty || localData === undefined);

      const hasOnlyDefault =
        localData && Object.keys(localData).length === 1 && localData.default;
      const isCategorized =
        categoryFields.length > 0 ||
        definition?.fields?.some(
          (f) => f.fieldId === "item_type" || f.fieldId === "category",
        );

      const needsRedistribution =
        isCategorized && hasOnlyDefault && localData.default.length > 0;
      const shouldSyncAction = shouldSync || needsRedistribution;

      if (shouldSyncAction) {
        // Use incomingData if shouldSync is true, otherwise use localData for redistribution
        const sourceData = shouldSync ? incomingData : localData;
        if (
          JSON.stringify(sourceData) !== JSON.stringify(localData) ||
          needsRedistribution
        ) {
          const allIncomingRows = Object.values(sourceData).flat();
          console.log(
            `QuestionTable [${tableId}] - Processing ${allIncomingRows.length} rows for redistribution`,
          );

          const redistributedData = {};
          const extractedCategories = {};

          // Auto-detect category fields if not provided
          let activeCategoryFields = categoryFields;
          if (!activeCategoryFields || activeCategoryFields.length === 0) {
            const potentialFields =
              definition?.fields?.filter(
                (f) =>
                  f.fieldId === "item_type" ||
                  f.fieldId === "category" ||
                  f.fieldType === "multiselect",
              ) || [];
            activeCategoryFields = potentialFields.map((f) => ({
              fieldId: f.fieldId,
              fieldLabel: f.fieldLabel,
            }));
            console.log(
              `QuestionTable [${tableId}] - Auto-detected Category Fields:`,
              activeCategoryFields,
            );
          }

          // Initialize extracted categories maps
          activeCategoryFields.forEach((cf) => {
            extractedCategories[cf.fieldId] = new Set();
          });

          // Process each row to categorize it
          allIncomingRows.forEach((row, idx) => {
            let categorized = false;
            console.log(
              `QuestionTable [${tableId}] - Row ${idx} values:`,
              row.values,
            );

            activeCategoryFields.forEach((cf) => {
              const rowVal = row.values?.[cf.fieldId];
              if (rowVal) {
                const cats = Array.isArray(rowVal) ? rowVal : [rowVal];
                console.log(
                  `QuestionTable [${tableId}] - Found category values for ${cf.fieldId}:`,
                  cats,
                );

                cats.forEach((cat) => {
                  if (cat) {
                    categorized = true;
                    if (!redistributedData[cat]) redistributedData[cat] = [];
                    // Avoid duplicates
                    if (
                      !redistributedData[cat].some(
                        (r) => (r._id || r.rowId) === (row._id || row.rowId),
                      )
                    ) {
                      redistributedData[cat].push(row);
                    }
                    extractedCategories[cf.fieldId].add(cat);
                  }
                });
              }
            });

            // If row didn't match any specific category, put in default
            if (!categorized) {
              console.log(
                `QuestionTable [${tableId}] - Row ${idx} could NOT be categorized, putting in 'default'`,
              );
              if (!redistributedData["default"])
                redistributedData["default"] = [];
              redistributedData["default"].push(row);
            }
          });

          const updated = {
            ...prev,
            [tableId]: redistributedData,
          };

          console.log(
            `QuestionTable [${tableId}] - Final Redistributed Map Keys:`,
            Object.keys(redistributedData),
          );

          // Sync the extracted categories back to top-level state
          activeCategoryFields.forEach((cf) => {
            const globalKey = `${tableId}_${cf.fieldId}`;
            const catsArray = Array.from(extractedCategories[cf.fieldId]);
            if (catsArray.length > 0) {
              console.log(
                `QuestionTable [${tableId}] - Auto-syncing categories for ${cf.fieldId}:`,
                catsArray,
              );
              updated[globalKey] = catsArray;
            }
          });

          return updated;
        }
      }

      let changed = false;
      const newTableData = localData
        ? { ...localData }
        : incomingData
          ? { ...incomingData }
          : {};

      allCatKeys.forEach((catKey) => {
        if (
          !newTableData[catKey] ||
          (Array.isArray(newTableData[catKey]) &&
            newTableData[catKey].length === 0)
        ) {
          newTableData[catKey] = [{ rowId: "row_1", values: {} }];
          changed = true;
        }
      });

      if (changed) return { ...prev, [tableId]: newTableData };
      return prev;
    });
  }, [
    tableId,
    allCatKeys.join(","),
    definition,
    loading,
    answersProp[tableId],
  ]);

  const setTableRow = (catKey, rowId, rowData) => {
    let updatedRowData = { ...rowData };
    const tableData = answersProp[tableId] || {};
    const groupRows = tableData[catKey] || [];
    const existingRow = groupRows.find((r) => r.rowId === rowId);

    // Identify changed key
    let changedFieldKey = null;
    if (existingRow?.values) {
      for (const key of Object.keys(updatedRowData)) {
        if (updatedRowData[key] !== existingRow.values[key]) {
          changedFieldKey = key;
          break;
        }
      }
    } else {
      for (const key of Object.keys(updatedRowData)) {
        if (updatedRowData[key] !== undefined && updatedRowData[key] !== "") {
          changedFieldKey = key;
          break;
        }
      }
    }

    // Validation: Waste sent for recycling cannot exceed Total recyclable waste
    if (
      updatedRowData.recyclable_waste !== undefined &&
      updatedRowData.waste_sent_recycling !== undefined
    ) {
      const total = parseFloat(updatedRowData.recyclable_waste);
      const sent = parseFloat(updatedRowData.waste_sent_recycling);
      if (!isNaN(total) && !isNaN(sent) && sent > total) {
        setPopupData({
          type: "error",
          title: "Validation Error",
          message:
            "Waste sent for recycling cannot exceed Total recyclable waste!",
        });
        setShowPopup(true);
        if (changedFieldKey) {
          updatedRowData[changedFieldKey] =
            existingRow?.values?.[changedFieldKey] !== undefined
              ? existingRow.values[changedFieldKey]
              : "";
        }
      }
    }

    // Validation for looped keys
    const loopKeys = Object.keys(updatedRowData).filter((k) =>
      k.startsWith("recyclable_waste_loop_"),
    );
    for (const key of loopKeys) {
      const suffix = key.replace("recyclable_waste_loop_", "");
      const sentKey = `waste_sent_recycling_loop_${suffix}`;
      if (updatedRowData[sentKey] !== undefined) {
        const total = parseFloat(updatedRowData[key]);
        const sent = parseFloat(updatedRowData[sentKey]);
        if (!isNaN(total) && !isNaN(sent) && sent > total) {
          setPopupData({
            type: "error",
            title: "Validation Error",
            message:
              "Waste sent for recycling cannot exceed Total recyclable waste!",
          });
          setShowPopup(true);
          if (changedFieldKey) {
            updatedRowData[changedFieldKey] =
              existingRow?.values?.[changedFieldKey] !== undefined
                ? existingRow.values[changedFieldKey]
                : "";
          }
        }
      }
    }

    const calculationGroups = {};

    fields.forEach((field) => {
      if (field.resultAction) {
        const action = field.resultAction;
        const sourceFields = fields.filter(
          (f) =>
            f.calculationType &&
            Array.isArray(f.calculationType) &&
            f.calculationType.includes(action),
        );

        if (sourceFields.length > 0) {
          if (!calculationGroups[action]) {
            calculationGroups[action] = [];
          }
          calculationGroups[action].push({
            targetField: field.fieldId,
            sourceFields: sourceFields.map((f) => f.fieldId),
            action,
          });
        }
      }
    });

    Object.values(calculationGroups).forEach((calculations) => {
      calculations.forEach(({ targetField, sourceFields, action }) => {
        let total = 0;
        let count = 0;
        let hasData = false;

        sourceFields.forEach((sourceId) => {
          const value = updatedRowData[sourceId];
          if (value !== undefined && value !== null && value !== "") {
            const numValue = parseFloat(value) || 0;
            total += numValue;
            count++;
            hasData = true;
          }
        });

        if (hasData) {
          if (action === "total") {
            updatedRowData[targetField] = total;
          } else if (action === "avg") {
            updatedRowData[targetField] =
              count > 0 ? Number((total / count).toFixed(4)) : 0;
          }
        } else {
          updatedRowData[targetField] = "";
        }
      });
    });

    setAnswers((prev) => {
      const tableData = prev[tableId] || {};
      const groupRows = tableData[catKey] || [];
      const idx = groupRows.findIndex((r) => r.rowId === rowId);
      const updated = [...groupRows];
      if (idx >= 0) updated[idx] = { ...updated[idx], values: updatedRowData };
      else updated.push({ rowId, values: updatedRowData });
      return { ...prev, [tableId]: { ...tableData, [catKey]: updated } };
    });
  };

  const getMultiselectDependency = (field, rowData) => {
    const multiselectDeps = field.displayCondition?.filter((cond) => {
      const triggerField = fields.find(
        (f) => f.fieldId === cond.triggerQuestionId,
      );
      const isTriggerMultiselect =
        triggerField?.fieldType === "multiselect" ||
        triggerField?.fieldType === "multi-select";

      if (!isTriggerMultiselect) return false;

      // Allow "exists" or "eq" for multiselect grouping
      if (cond.operator !== "exists" && cond.operator !== "eq") return false;

      if (
        isCategorized &&
        categoryFields.some((cf) => cf.fieldId === cond.triggerQuestionId)
      ) {
        return false;
      }

      return true;
    });

    const aggregatedFields = [];
    const allSelectedItems = [];

    if (multiselectDeps) {
      multiselectDeps.forEach((dep) => {
        const triggerField = fields.find(
          (f) => f.fieldId === dep.triggerQuestionId,
        );
        const selected = rowData[dep.triggerQuestionId] || [];

        if (Array.isArray(selected) && selected.length > 0) {
          aggregatedFields.push({
            field: triggerField,
            selectedItems: selected,
          });
          allSelectedItems.push(...selected);
        }
      });
    }

    if (aggregatedFields.length > 0) {
      return {
        fields: aggregatedFields,
        allSelectedItems: [...new Set(allSelectedItems)],
      };
    }
    return null;
  };

  if (loading)
    return (
      <div className="p-4 bg-gray-50 border rounded animate-pulse">
        Loading Table...
      </div>
    );
  if (!definition) return null;

  return (
    <div className="space-y-6">
      {isCategorized &&
        categoryFields.map((cf) => (
          <div
            key={cf.fieldId}
            className="mb-4 p-4 bg-blue-50 border-l-4 border-blue-500 rounded"
          >
            <GuidanceBlock guidance={cf.guidance} />
            {cf.fieldTitle && cf.fieldTitle.length > 0 && (
              <p className="block text-sm font-semibold text-[#06513D] mb-2 mt-4 border-b pb-1">
                {cf.fieldTitle[0]}
              </p>
            )}
            <label className="block font-bold mb-2">{cf.fieldLabel}</label>
            <QuestionInput
              rowData={answersProp}
              isEmployee={isEmployee}
              field={cf}
              value={
                answersProp[`${tableId}_${cf.fieldId}`] ||
                answersProp[cf.fieldId] ||
                []
              }
              onChange={(val) =>
                setAnswers((prev) => ({
                  ...prev,
                  [`${tableId}_${cf.fieldId}`]: val,
                }))
              }
            />
          </div>
        ))}

      {allCatKeys.map((catKey) => {
        const tableData = answersProp[tableId] || question.value || {};
        const rows =
          tableData[catKey] && tableData[catKey].length > 0
            ? tableData[catKey]
            : catKey !== "default" && tableData["default"]
              ? tableData["default"]
              : tableData[catKey] || [];

        console.log(
          `QuestionTable [${tableId}] - Category [${catKey}] - Rows:`,
          rows.length,
        );

        let catLabel = catKey;
        if (catKey === "default") {
          catLabel = ""; // No label for global fields
        } else {
          for (const cf of categoryFields) {
            const opt = cf.options?.find((o) => o.value === catKey);
            if (opt) {
              catLabel = opt.label;
              break;
            }
          }
        }

        const isDefaultInCategorized = isCategorized && catKey === "default";

        return (
          <div
            key={catKey}
            className={
              isDefaultInCategorized
                ? ""
                : "border-2 border-gray-200 rounded-xl p-4 bg-white transition-all"
            }
          >
            {catLabel && (
              <h3 className="text-lg font-bold mb-4 uppercase text-teal-700">
                {catLabel}
              </h3>
            )}
            {rows.map((row) => {
              const context = { ...row.values };
              if (isCategorized) {
                const activeCategoryFields = categoryFields.filter((cf) => {
                  const vals = answersProp[`${tableId}_${cf.fieldId}`] || [];
                  return vals.includes(catKey);
                });
                categoryFields.forEach((cf) => {
                  if (activeCategoryFields.includes(cf)) {
                    context[cf.fieldId] = [catKey];
                  } else {
                    context[cf.fieldId] = [];
                  }
                });
              }

              return (
                <div
                  key={row.rowId}
                  className={
                    isDefaultInCategorized
                      ? "mb-6"
                      : "mb-6 p-4 border rounded-lg bg-gray-50 relative"
                  }
                >
                  <div className="space-y-4">
                    {(() => {
                      const renderedFieldIds = new Set();
                      return fields.map((field, idx) => {
                        // 1. Skip Category Choice Fields (Rendered at top)
                        if (
                          isCategorized &&
                          categoryFields.find(
                            (cf) => cf.fieldId === field.fieldId,
                          )
                        )
                          return null;

                        // 2. Filter Global vs Category Fields
                        const isGlobal = globalFields.find(
                          (gf) => gf.fieldId === field.fieldId,
                        );
                        if (isCategorized) {
                          if (catKey === "default" && !isGlobal) return null;
                          if (catKey !== "default" && isGlobal) return null;
                        }

                        if (renderedFieldIds.has(field.fieldId)) return null;
                        if (
                          !evalCondition(field, answersProp, {
                            rowData: context,
                          })
                        )
                          return null;

                        const isRepeater =
                          (field.fieldType === "number" ||
                            field.fieldType === "count") &&
                          parseInt(
                            getValueFromRow(row.values, field.fieldId, catKey),
                          ) > 0 &&
                          fields.some((other) =>
                            other.displayCondition?.some(
                              (c) =>
                                c.triggerQuestionId === field.fieldId ||
                                c.triggerFieldId === field.fieldId,
                            ),
                          );

                        if (isRepeater) {
                          const repeaterCount = parseInt(
                            getValueFromRow(row.values, field.fieldId, catKey),
                          );
                          const loopMemberIds = new Set();
                          let previousSize = 0;

                          fields.forEach((f) => {
                            const dependsOnRoot = f.displayCondition?.some(
                              (c) =>
                                c.triggerQuestionId === field.fieldId ||
                                c.triggerFieldId === field.fieldId,
                            );
                            if (dependsOnRoot) loopMemberIds.add(f.fieldId);
                          });

                          while (loopMemberIds.size > previousSize) {
                            previousSize = loopMemberIds.size;
                            fields.forEach((f) => {
                              if (!loopMemberIds.has(f.fieldId)) {
                                const dependsOnMember =
                                  f.displayCondition?.some((c) => {
                                    const tId =
                                      c.triggerQuestionId || c.triggerFieldId;
                                    return loopMemberIds.has(tId);
                                  });
                                if (dependsOnMember)
                                  loopMemberIds.add(f.fieldId);
                              }
                            });
                          }

                          const childFields = fields.filter((f) =>
                            loopMemberIds.has(f.fieldId),
                          );

                          renderedFieldIds.add(field.fieldId);
                          childFields.forEach((cf) =>
                            renderedFieldIds.add(cf.fieldId),
                          );

                          return (
                            <div key={field.fieldId} className="space-y-4">
                              <div>
                                <GuidanceBlock guidance={field.guidance} />
                                {field.fieldTitle &&
                                  field.fieldTitle.length > 0 && (
                                    <p className="block text-sm font-semibold text-[#06513D] mb-2 mt-4 border-b pb-1">
                                      {field.fieldTitle[0]}
                                    </p>
                                  )}
                                <label className="block text-sm font-medium mb-1">
                                  {field.fieldLabel}
                                </label>
                                <QuestionInput
                                  rowData={row.values}
                                  isEmployee={isEmployee}
                                  field={field}
                                  value={getValueFromRow(
                                    row.values,
                                    field.fieldId,
                                    catKey,
                                  )}
                                  disabled={[
                                    "total_annual_consumption",
                                    "avg_annual_consumption",
                                  ].includes(field.fieldId)}
                                  onChange={(val) =>
                                    setTableRow(catKey, row.rowId, {
                                      ...row.values,
                                      [field.fieldId]: val,
                                    })
                                  }
                                />
                              </div>
                              <div className="mt-4 space-y-6">
                                {Array.from(
                                  { length: Math.min(repeaterCount, 100) },
                                  (_, i) => (
                                    <div
                                      key={`${row.rowId}_${field.fieldId}_loop_${i}`}
                                      className="p-4 border-2 border-teal-100 rounded-xl bg-teal-50/10"
                                    >
                                      <div className="text-teal-600 font-bold mb-4 uppercase text-sm tracking-wider">
                                        {field.fieldId.includes("vehicle") ||
                                        field.fieldLabel
                                          .toLowerCase()
                                          .includes("vehicle")
                                          ? "Vehicle"
                                          : "Entry"}{" "}
                                        {i + 1}
                                      </div>
                                      {childFields.map((cf) => {
                                        const valueKey = `${cf.fieldId}${TRIP_SEP}${i}`;
                                        const loopValues = { ...context };

                                        // Check for backend-specific nested details array (e.g., number_of_bod_details)
                                        const repeaterId = field.fieldId;
                                        const detailsKey = `${repeaterId}_details`;
                                        const nestedDetails =
                                          row.values[detailsKey];

                                        let finalValue = row.values[valueKey];

                                        if (
                                          finalValue === undefined &&
                                          Array.isArray(nestedDetails) &&
                                          nestedDetails[i]
                                        ) {
                                          finalValue =
                                            nestedDetails[i][cf.fieldId];
                                        }

                                        childFields.forEach((sibling) => {
                                          const sKey = `${sibling.fieldId}${TRIP_SEP}${i}`;
                                          if (row.values[sKey] !== undefined) {
                                            loopValues[sibling.fieldId] =
                                              row.values[sKey];
                                          } else if (
                                            Array.isArray(nestedDetails) &&
                                            nestedDetails[i] &&
                                            nestedDetails[i][
                                              sibling.fieldId
                                            ] !== undefined
                                          ) {
                                            loopValues[sibling.fieldId] =
                                              nestedDetails[i][sibling.fieldId];
                                          }
                                        });

                                        if (
                                          !evalCondition(cf, answersProp, {
                                            rowData: {
                                              ...loopValues,
                                              repeatingParentId: field.fieldId,
                                            },
                                          })
                                        )
                                          return null;

                                        const loopMsDep =
                                          getMultiselectDependency(
                                            cf,
                                            loopValues,
                                            [], // questionsList
                                            i, // tripIndex
                                          );

                                        if (loopMsDep) {
                                          const {
                                            fields: msFields,
                                            allSelectedItems,
                                          } = loopMsDep;

                                          return (
                                            <div
                                              key={valueKey}
                                              className="mb-4 border-l-2 border-teal-50 pl-4"
                                            >
                                              <div className="space-y-4">
                                                {allSelectedItems.map(
                                                  (itemValue) => {
                                                    const sourceMs =
                                                      msFields.find((mf) =>
                                                        mf.selectedItems.includes(
                                                          itemValue,
                                                        ),
                                                      );
                                                    const opt =
                                                      sourceMs?.field.options?.find(
                                                        (o) =>
                                                          o.value === itemValue,
                                                      );
                                                    const label =
                                                      opt?.label || itemValue;
                                                    const splitValueKey = `${cf.fieldId}${TRIP_SEP}${i}_${itemValue}`;

                                                    let splitValue =
                                                      getValueFromRow(
                                                        row.values,
                                                        `${cf.fieldId}${TRIP_SEP}${i}`,
                                                        itemValue,
                                                      );

                                                    if (
                                                      splitValue ===
                                                        undefined &&
                                                      Array.isArray(
                                                        nestedDetails,
                                                      ) &&
                                                      nestedDetails[i]
                                                    ) {
                                                      const fieldData =
                                                        nestedDetails[i][
                                                          cf.fieldId
                                                        ];
                                                      if (
                                                        Array.isArray(fieldData)
                                                      ) {
                                                        // Search for the specific itemValue (key) in the array of objects
                                                        const match =
                                                          fieldData.find(
                                                            (item) =>
                                                              (item &&
                                                                item.key ===
                                                                  itemValue) ||
                                                              (item &&
                                                                item.id ===
                                                                  itemValue) ||
                                                              (item &&
                                                                item.value ===
                                                                  itemValue),
                                                          );
                                                        splitValue = match
                                                          ? match.value !==
                                                            undefined
                                                            ? match.value
                                                            : match.answer
                                                          : undefined;
                                                      } else {
                                                        splitValue =
                                                          nestedDetails[i][
                                                            `${cf.fieldId}_${itemValue}`
                                                          ] || fieldData;
                                                      }
                                                    }

                                                    return (
                                                      <div
                                                        key={itemValue}
                                                        className="w-full"
                                                      >
                                                        {cf.fieldTitle &&
                                                          cf.fieldTitle.length >
                                                            0 && (
                                                            <div className="mb-2 pb-1 border-b border-gray-100">
                                                              <p className="text-[13px] font-semibold text-[#06513D]">
                                                                {
                                                                  cf
                                                                    .fieldTitle[0]
                                                                }
                                                              </p>
                                                            </div>
                                                          )}
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                                          {cf.fieldLabel} (
                                                          {label})
                                                        </label>
                                                        <div className="flex items-center gap-2">
                                                          <div className="flex-grow">
                                                            <QuestionInput
                                                              isEmployee={
                                                                isEmployee
                                                              }
                                                              field={cf}
                                                              value={splitValue}
                                                              onChange={(val) =>
                                                                setTableRow(
                                                                  catKey,
                                                                  row.rowId,
                                                                  {
                                                                    ...row.values,
                                                                    [splitValueKey]:
                                                                      val,
                                                                  },
                                                                )
                                                              }
                                                            />
                                                          </div>
                                                        </div>
                                                      </div>
                                                    );
                                                  },
                                                )}
                                              </div>
                                              {(cf.fieldLabel
                                                .toLowerCase()
                                                .includes("percentage") ||
                                                cf.fieldType ===
                                                  "percentage") && (
                                                <div className="mt-2 text-right">
                                                  {(() => {
                                                    const total =
                                                      allSelectedItems.reduce(
                                                        (sum, item) => {
                                                          let sVal =
                                                            getValueFromRow(
                                                              row.values,
                                                              `${cf.fieldId}${TRIP_SEP}${i}`,
                                                              item,
                                                            );
                                                          if (
                                                            sVal ===
                                                              undefined &&
                                                            Array.isArray(
                                                              nestedDetails,
                                                            ) &&
                                                            nestedDetails[i]
                                                          ) {
                                                            sVal =
                                                              nestedDetails[i][
                                                                `${cf.fieldId}_${item}`
                                                              ] ||
                                                              nestedDetails[i][
                                                                cf.fieldId
                                                              ];
                                                          }
                                                          return (
                                                            sum +
                                                            (parseFloat(sVal) ||
                                                              0)
                                                          );
                                                        },
                                                        0,
                                                      );
                                                    const isCorrect =
                                                      total === 100;
                                                    const isVisible =
                                                      total !== 0;

                                                    return (
                                                      <div className="flex justify-between items-center text-sm font-bold p-3 bg-white border border-gray-200 rounded-lg shadow-sm w-full">
                                                        <span>
                                                          Total Percentage:
                                                        </span>
                                                        {isVisible &&
                                                          !isCorrect && (
                                                            <span className="text-xs text-red-500 font-bold animate-pulse">
                                                              ⚠️ Total must be
                                                              100%
                                                            </span>
                                                          )}
                                                        <span
                                                          className={`text-lg transition-all duration-300 ${
                                                            isCorrect
                                                              ? "text-green-600 scale-110"
                                                              : "text-red-600"
                                                          }`}
                                                        >
                                                          {total.toFixed(1)}%
                                                        </span>
                                                      </div>
                                                    );
                                                  })()}
                                                </div>
                                              )}
                                            </div>
                                          );
                                        }

                                        return (
                                          <div key={valueKey} className="mb-3">
                                            <GuidanceBlock
                                              guidance={cf.guidance}
                                            />
                                            {cf.fieldTitle &&
                                              cf.fieldTitle.length > 0 && (
                                                <p className="block text-sm font-semibold text-[#06513D] mb-2 mt-4 border-b pb-1">
                                                  {cf.fieldTitle[0]}
                                                </p>
                                              )}
                                            <label className="block text-sm font-medium mb-1">
                                              {cf.fieldLabel}
                                            </label>
                                            <QuestionInput
                                              rowData={row.values}
                                              isEmployee={isEmployee}
                                              field={cf}
                                              value={finalValue}
                                              onChange={(val) =>
                                                setTableRow(catKey, row.rowId, {
                                                  ...row.values,
                                                  [valueKey]: val,
                                                })
                                              }
                                            />
                                          </div>
                                        );
                                      })}
                                    </div>
                                  ),
                                )}
                              </div>
                            </div>
                          );
                        }

                        // Paired logic
                        const isInput = (f) =>
                          ["number", "percentage", "select", "text"].includes(
                            f?.fieldType,
                          );
                        const isNumOrPerc = (f) =>
                          f?.fieldType === "number" ||
                          f?.fieldType === "percentage";
                        const nextField = fields[idx + 1];

                        const canPairInputWithFile =
                          isInput(field) &&
                          !getMultiselectDependency(field, row.values) &&
                          nextField &&
                          nextField.fieldType === "file" &&
                          !getMultiselectDependency(nextField, row.values) &&
                          !renderedFieldIds.has(nextField.fieldId) &&
                          evalCondition(nextField, answersProp, {
                            rowData: context,
                            tripIndex: null,
                          });

                        if (canPairInputWithFile) {
                          renderedFieldIds.add(field.fieldId);
                          renderedFieldIds.add(nextField.fieldId);

                          return (
                            <div
                              key={`pair_file_container_${field.fieldId}`}
                              className="mt-6 border-t pt-4 first:border-0 first:pt-0"
                            >
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                  <label className="block text-sm font-medium mb-1">
                                    {field.fieldLabel}
                                  </label>
                                  <QuestionInput
                                    rowData={row.values}
                                    isEmployee={isEmployee}
                                    field={field}
                                    value={getValueFromRow(
                                      row.values,
                                      field.fieldId,
                                      catKey,
                                    )}
                                    onChange={(val) =>
                                      setTableRow(catKey, row.rowId, {
                                        ...row.values,
                                        [field.fieldId]: val,
                                      })
                                    }
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium mb-1">
                                    {nextField.fieldLabel}
                                  </label>
                                  <QuestionInput
                                    rowData={row.values}
                                    isEmployee={isEmployee}
                                    field={nextField}
                                    value={getValueFromRow(
                                      row.values,
                                      nextField.fieldId,
                                      catKey,
                                    )}
                                    onChange={(val) =>
                                      setTableRow(catKey, row.rowId, {
                                        ...row.values,
                                        [nextField.fieldId]: val,
                                      })
                                    }
                                  />
                                </div>
                              </div>
                            </div>
                          );
                        }

                        const canPairWithNext =
                          isNumOrPerc(field) &&
                          !getMultiselectDependency(field, row.values) &&
                          nextField &&
                          isNumOrPerc(nextField) &&
                          !getMultiselectDependency(nextField, row.values) &&
                          !renderedFieldIds.has(nextField.fieldId) &&
                          evalCondition(nextField, answersProp, {
                            rowData: context,
                            tripIndex: null,
                          });

                        if (canPairWithNext) {
                          renderedFieldIds.add(field.fieldId);
                          renderedFieldIds.add(nextField.fieldId);

                          const hasTitles =
                            (field.fieldTitle && field.fieldTitle.length > 0) ||
                            (nextField.fieldTitle &&
                              nextField.fieldTitle.length > 0);
                          const hasGuidance =
                            (field.guidance && field.guidance.length > 0) ||
                            (nextField.guidance &&
                              nextField.guidance.length > 0);

                          return (
                            <div
                              key={`pair_container_${field.fieldId}`}
                              className="mt-6 border-t pt-4 first:border-0 first:pt-0"
                            >
                              {hasTitles && (
                                <div className="mb-2">
                                  {field.fieldTitle?.[0] &&
                                  nextField.fieldTitle?.[0] ? (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                      <p className="text-sm font-semibold text-gray-800 border-b pb-1">
                                        {field.fieldTitle[0]}
                                      </p>
                                      <p className="text-sm font-semibold text-gray-800 border-b pb-1">
                                        {nextField.fieldTitle[0]}
                                      </p>
                                    </div>
                                  ) : (
                                    <p className="text-sm font-semibold text-gray-800 border-b pb-1">
                                      {field.fieldTitle?.[0] ||
                                        nextField.fieldTitle?.[0]}
                                    </p>
                                  )}
                                </div>
                              )}

                              {hasGuidance && (
                                <div className="mb-3">
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <GuidanceBlock guidance={field.guidance} />
                                    <GuidanceBlock
                                      guidance={nextField.guidance}
                                    />
                                  </div>
                                </div>
                              )}

                              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                  <label className="block text-sm font-medium mb-1">
                                    {field.fieldLabel}
                                  </label>
                                  <QuestionInput
                                    rowData={row.values}
                                    isEmployee={isEmployee}
                                    field={field}
                                    value={getValueFromRow(
                                      row.values,
                                      field.fieldId,
                                      catKey,
                                    )}
                                    disabled={[
                                      "total_annual_consumption",
                                      "avg_annual_consumption",
                                    ].includes(field.fieldId)}
                                    onChange={(val) =>
                                      setTableRow(catKey, row.rowId, {
                                        ...row.values,
                                        [field.fieldId]: val,
                                      })
                                    }
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium mb-1">
                                    {nextField.fieldLabel}
                                  </label>
                                  <QuestionInput
                                    rowData={row.values}
                                    isEmployee={isEmployee}
                                    field={nextField}
                                    value={getValueFromRow(
                                      row.values,
                                      nextField.fieldId,
                                      catKey,
                                    )}
                                    disabled={[
                                      "total_annual_consumption",
                                      "avg_annual_consumption",
                                    ].includes(nextField.fieldId)}
                                    onChange={(val) =>
                                      setTableRow(catKey, row.rowId, {
                                        ...row.values,
                                        [nextField.fieldId]: val,
                                      })
                                    }
                                  />
                                </div>
                              </div>
                            </div>
                          );
                        }

                        // Multiselect dependency grouping
                        renderedFieldIds.add(field.fieldId);
                        const multiselectDep = getMultiselectDependency(
                          field,
                          row.values,
                        );

                        if (multiselectDep) {
                          const relatedQuestions = fields.filter((q) => {
                            if (
                              !evalCondition(q, answersProp, {
                                rowData: context,
                              })
                            )
                              return false;
                            const qDep = getMultiselectDependency(
                              q,
                              row.values,
                            );
                            if (!qDep) return false;
                            const currentTriggerIds = multiselectDep.fields
                              .map((f) => f.field.fieldId)
                              .sort()
                              .join(",");
                            const qTriggerIds = qDep.fields
                              .map((f) => f.field.fieldId)
                              .sort()
                              .join(",");
                            return currentTriggerIds === qTriggerIds;
                          });

                          const isLeader =
                            relatedQuestions.length > 0 &&
                            relatedQuestions[0].fieldId === field.fieldId;
                          if (!isLeader) return null;

                          const {
                            fields: multiselectFields,
                            allSelectedItems,
                          } = multiselectDep;
                          relatedQuestions.forEach((q) =>
                            renderedFieldIds.add(q.fieldId),
                          );

                          return (
                            <div
                              key={`group_${field.fieldId}`}
                              className="mb-4 pl-4 border-l-2 border-teal-50"
                            >
                              <div className="rounded-lg border border-gray-200 shadow-sm bg-white divide-y divide-gray-200">
                                {allSelectedItems.map((itemValue) => {
                                  const sourceMultiselect =
                                    multiselectFields.find((mf) =>
                                      mf.selectedItems.includes(itemValue),
                                    );
                                  const option =
                                    sourceMultiselect?.field.options?.find(
                                      (opt) => opt.value === itemValue,
                                    );
                                  const optionLabel =
                                    option?.label || itemValue;

                                  return (
                                    <div
                                      key={itemValue}
                                      className="p-6 transition-all border-b last:border-b-0 hover:bg-gray-50/50"
                                    >
                                      <div className="text-teal-700 font-bold mb-4 uppercase text-xs tracking-wider flex items-center gap-2">
                                        <div className="w-1.5 h-1.5 rounded-full bg-teal-500" />
                                        {optionLabel}
                                      </div>
                                      <div className="grid gap-6 pl-4 border-l-2 border-teal-50/50">
                                        {relatedQuestions
                                          .filter((q) => {
                                            const qDep =
                                              getMultiselectDependency(
                                                q,
                                                row.values,
                                              );

                                            // Only render if the question depends specifically on THIS itemValue
                                            if (
                                              !qDep ||
                                              !qDep.allSelectedItems?.includes(
                                                itemValue,
                                              )
                                            ) {
                                              return false;
                                            }

                                            const scopedContext = {
                                              ...context,
                                            };
                                            (qDep.fields || []).forEach(
                                              (fd) => {
                                                scopedContext[
                                                  fd.field.fieldId
                                                ] = [itemValue];
                                              },
                                            );
                                            return evalCondition(
                                              q,
                                              answersProp,
                                              {
                                                rowData: scopedContext,
                                              },
                                            );
                                          })
                                          .map((q) => {
                                            const valueKey = `${q.fieldId}_${itemValue}`;
                                            return (
                                              <div
                                                key={q.fieldId}
                                                className="w-full"
                                              >
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                  {q.fieldLabel} ({optionLabel})
                                                </label>
                                                <QuestionInput
                                                  rowData={row.values}
                                                  isEmployee={isEmployee}
                                                  field={q}
                                                  value={getValueFromRow(
                                                    row.values,
                                                    q.fieldId,
                                                    itemValue,
                                                  )}
                                                  onChange={(val) =>
                                                    setTableRow(
                                                      catKey,
                                                      row.rowId,
                                                      {
                                                        ...row.values,
                                                        [valueKey]: val,
                                                      },
                                                    )
                                                  }
                                                />
                                              </div>
                                            );
                                          })}
                                      </div>
                                    </div>
                                  );
                                })}

                                {relatedQuestions.some(
                                  (q) =>
                                    q.fieldLabel
                                      .toLowerCase()
                                      .includes("percentage") ||
                                    q.fieldType === "percentage",
                                ) && (
                                  <div className="bg-gray-50 p-6 space-y-4">
                                    {relatedQuestions.map((q) => {
                                      if (
                                        !q.fieldLabel
                                          .toLowerCase()
                                          .includes("percentage") &&
                                        q.fieldType !== "percentage"
                                      )
                                        return null;
                                      const total = allSelectedItems.reduce(
                                        (sum, itemValue) =>
                                          sum +
                                          (parseFloat(
                                            getValueFromRow(
                                              row.values,
                                              q.fieldId,
                                              itemValue,
                                            ),
                                          ) || 0),
                                        0,
                                      );
                                      const isCorrect = total === 100;
                                      const isVisible = total !== 0;

                                      return (
                                        <div
                                          key={q.fieldId}
                                          className="flex justify-between items-center text-sm font-bold p-3 bg-white border border-gray-200 rounded-lg shadow-sm w-full"
                                        >
                                          <span>Total Percentage:</span>
                                          {isVisible && !isCorrect && (
                                            <span className="text-xs text-red-500 font-bold animate-pulse">
                                              ⚠️ Total must be 100%
                                            </span>
                                          )}
                                          <span
                                            className={`text-lg transition-all duration-300 ${
                                              isCorrect
                                                ? "text-green-600 scale-110"
                                                : "text-red-600"
                                            }`}
                                          >
                                            {total.toFixed(1)}%
                                          </span>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        }

                        return (
                          <div key={field.fieldId} className="mt-4">
                            <GuidanceBlock guidance={field.guidance} />
                            {field.fieldTitle &&
                              field.fieldTitle.length > 0 && (
                                <p className="block text-sm font-semibold text-[#06513D] mb-2 mt-4 border-b pb-1">
                                  {field.fieldTitle[0]}
                                </p>
                              )}
                            <label className="block text-sm font-medium mb-1">
                              {field.fieldLabel}
                            </label>
                            <QuestionInput
                              rowData={row.values}
                              isEmployee={isEmployee}
                              field={field}
                              value={getValueFromRow(
                                row.values,
                                field.fieldId,
                                catKey,
                              )}
                              onChange={(val) =>
                                setTableRow(catKey, row.rowId, {
                                  ...row.values,
                                  [field.fieldId]: val,
                                })
                              }
                            />
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}
      {showPopup &&
        ReactDOM.createPortal(
          <div
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 99999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(0,0,0,0.35)",
              backdropFilter: "blur(4px)",
              WebkitBackdropFilter: "blur(4px)",
            }}
          >
            <PopupCard
              type={popupData.type}
              title={popupData.title}
              message={popupData.message}
            />
          </div>,
          document.body,
        )}
    </div>
  );
};

export default QuestionTable;
